import { authService } from '@/services/auth.service';
import { deviceId, wipeLocalDb } from '@/db/pos-database';
import {
  importDeviceKey,
  setActiveKey,
  clearKey,
  hasActiveKey,
} from '@/db/crypto';
import {
  getUnsyncedCount,
  processQueue,
  retryDeadEntries,
} from '@/services/sync-queue.service';
import { useAppHealthStore } from '@/stores/use-app-health-store';
import { countWaitingParkedBills, pushLocalParkedBills } from '@/services/parked-bills.service';

/**
 * G2.27 — Device-session lifecycle for IndexedDB-at-rest encryption.
 *
 * Called from the auth store:
 *   - `beginDeviceSession()` after a successful login or session rehydrate
 *   - `endDeviceSession()` during logout
 *
 * On begin: fetches the per-device KEK from the backend, imports it as a
 * non-extractable `CryptoKey`, and installs it as the active encryption
 * key for `crypto.ts`. The raw KEK is garbage-collected immediately
 * after `importDeviceKey` returns — only the opaque `CryptoKey` handle
 * survives.
 *
 * On end: tells the backend to invalidate the KEK (so it cannot be
 * re-used if an attacker later steals our refresh token), then clears
 * the in-memory key. The caller is responsible for wiping IndexedDB
 * afterward — see PR 2 for the purge flow.
 *
 * Rotation counter: the server response includes a `counter` that
 * increments every time a fresh KEK is issued (after an unenroll).
 * We stash it in localStorage so a cross-session client can detect a
 * remote unenroll — if the counter jumps, the previously-encrypted
 * data is orphaned and must be purged.
 */

const COUNTER_KEY = 'bbpos_kek_counter';

export interface DeviceSessionResult {
  enrolled: boolean;
  /** True when the server issued a fresh KEK (counter changed) — caller should purge. */
  rotated: boolean;
  /** T17: a rotation found unsynced bills, so local data was kept and the wipe delayed. */
  wipeDeferred: boolean;
  counter: number;
}

/** Unsynced bills plus parks not yet uploaded (gst-parked 120). */
async function unsavedCount(): Promise<number> {
  return (await getUnsyncedCount()) + (await countWaitingParkedBills().catch(() => 0));
}

/**
 * T17 (owner decision): a rotation never wipes unsynced bills — nor, since
 * gst-parked 120, parked bills still on this till. Push them with the old key
 * while it is still in memory; true when nothing is left on the device to lose.
 */
async function flushBeforeWipe(): Promise<boolean> {
  if ((await unsavedCount()) === 0) return true;
  if (hasActiveKey()) {
    await retryDeadEntries();
    await pushLocalParkedBills().catch(() => undefined);
  }
  return (await unsavedCount()) === 0;
}

/**
 * Enroll this device with the backend and install the encryption key.
 *
 * Idempotent: calling twice within the KEK TTL returns the same KEK and
 * `rotated=false`. The caller can invoke this on every login/rehydrate
 * without worrying about duplicate work.
 *
 * Failure mode: if the network is down or the server returns an error,
 * this function resolves with `enrolled=false` rather than throwing.
 * The app remains usable without encryption; the next successful
 * enrollment will install the key. This matches the offline-first
 * philosophy of the POS — we never block the cashier on a remote call.
 */
export async function beginDeviceSession(): Promise<DeviceSessionResult> {
  try {
    const { kek, counter } = await authService.enrollDevice(deviceId);
    const key = await importDeviceKey(kek);

    const prev = parseInt(localStorage.getItem(COUNTER_KEY) ?? '0', 10);
    const rotated = prev !== 0 && prev !== counter;

    if (rotated && !(await flushBeforeWipe())) {
      // Bills are still unsynced: delay the wipe and tell the cashier. The
      // old counter stays, so the next session start tries again. With the
      // old key in memory it stays active and the queue stays readable;
      // after a page reload it is gone, so new work uses the new key and the
      // old rows wait, unread, for the sign-in that wrote them.
      const oldKeyKept = hasActiveKey();
      if (!oldKeyKept) setActiveKey(key);
      const count = await unsavedCount();
      useAppHealthStore.getState().setDeviceKeyWarning(
        `Sync pending bills first: this till's device key changed while ${count} offline item(s) were not on the server. ` +
          (oldKeyKept
            ? 'They stay on this till and its old data is cleared only once they sync.'
            : 'They stay on this till. Items saved under an earlier sign-in sync when that sign-in returns here.'),
      );
      // With the new key, rows it cannot read are parked DEAD (listed, counted).
      if (!oldKeyKept) void processQueue();
      return { enrolled: true, rotated, wipeDeferred: true, counter };
    }

    // Rotation detected — the server issued a new KEK (because the prior
    // one was explicitly unenrolled). Any ciphertext already on disk was
    // encrypted with the old KEK and is now orphaned. Purge-and-resync.
    if (rotated) {
      // eslint-disable-next-line no-console
      console.info('[device-session] rotation detected, wiping local DB');
      await wipeLocalDb();
    }
    setActiveKey(key);
    localStorage.setItem(COUNTER_KEY, String(counter));
    useAppHealthStore.getState().setDeviceKeyWarning(null);
    // Rows queued before the key was installed (page load) are readable now.
    void processQueue();

    return { enrolled: true, rotated, wipeDeferred: false, counter };
  } catch (err) {
    // Do not brick login on enrollment failure — encryption will resume
    // on the next successful call. Log for observability only.
    // eslint-disable-next-line no-console
    console.warn('[device-session] enrollment failed:', err);
    return { enrolled: false, rotated: false, wipeDeferred: false, counter: 0 };
  }
}

/**
 * Invalidate this device's KEK on the server and clear the local key.
 *
 * Always clears the local key, even if the remote call fails (e.g.
 * during an offline logout). The server's KEK will expire via TTL if
 * we never reach it.
 */
export async function endDeviceSession(): Promise<void> {
  try {
    await authService.unenrollDevice(deviceId);
  } catch {
    // Best-effort — local state wins regardless.
  }
  clearKey();
  localStorage.removeItem(COUNTER_KEY);
  // Purge-and-resync: drop local ciphertext since the key that encrypted
  // it is now invalidated on the server. Next login re-enrols with a
  // fresh KEK and the catalog re-syncs from the server.
  try {
    await wipeLocalDb();
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[device-session] local DB wipe failed:', err);
  }
}

/** Test/debug helper. */
export function isDeviceSessionActive(): boolean {
  return hasActiveKey();
}
